import { VStack, HStack, Stack } from '@chakra-ui/react'
import React, { useEffect } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { getTodayISO, getPastDate } from '../../utils'
import { useAccountDetails } from '../../hooks'
import { AccountInformation } from '../AccountInformation'
import { PositionsTable } from 'components/PositionsTable'
import { AccountSearchBar } from 'components/AccountSearch'
import { BalancesTable } from 'components/BalancesTable'
import { MarginsTable } from 'components/MarginsTable'
import { RecentActivityTable } from 'components/RecentActivityTable'
import { AccountSummaryHeader } from 'components/AccountSummaryHeader'
import { ApexLogin } from 'containers/ApexLogin'
import { useAuth } from '../../hooks'

interface MatchProps {
  accountNumber: string
  endDate: string
  processDate?: string
  startDate: string
}

export interface AccountSummaryPageProps
  extends RouteComponentProps<MatchProps> {}

//TODO AP placeholder, replace as necessary
const AccountSummaryPage = ({ match }: AccountSummaryPageProps) => {
  const {
    accountDetailsState: {
      accountDetails,
      loadingResult: loadingAccountDetails,
    },
    accountPositionsState: { accountPositions, loadingPositions },
    activitiesState: { activities, processDate, loadingActivities },
    balanceState: { balances, loadingBalances },
    marginCallsState: { marginCalls, loadingMarginCalls },
    marginsState: { margins, loadingMargins },
    amountAvailableState: { amountAvailable, loadingAmountAvailable },
    securemailState: { securemailLinks, loadingSecuremailLinks },
    getAccountDetails,
    getAccountNotes,
    getAccountOwners,
    getActivities,
    getBalances,
    getCorrespondent,
    getTradingRestrictions,
    getAccountPositions,
    getMarginCalls,
    getMargins,
    getAmountAvailable,
    getSecuremailLinks,
  } = useAccountDetails()

  useEffect(() => {
    const today = getTodayISO()
    const startDate = getPastDate(today, 5)

    getAccountDetails(match.params.accountNumber)
    getActivities(
      match.params.accountNumber,
      (match.params.startDate = startDate),
      (match.params.endDate = today)
    )
    getAccountOwners(match.params.accountNumber)
    getAccountNotes(match.params.accountNumber)
    getAccountPositions(match.params.accountNumber, match.params.processDate)
    getBalances(match.params.accountNumber, match.params.processDate)
    getCorrespondent(match.params.accountNumber)
    getMarginCalls(match.params.accountNumber)
    getMargins(match.params.accountNumber)
    getTradingRestrictions(match.params.accountNumber)
    getAmountAvailable(match.params.accountNumber)
    getSecuremailLinks(match.params.accountNumber, 'STATEMENT')
    getSecuremailLinks(match.params.accountNumber, 'CONFIRM')
    getSecuremailLinks(match.params.accountNumber, 'TAX')
  }, [
    match.params.accountNumber,
    match.params.endDate,
    match.params.processDate,
    match.params.startDate,
    getAccountDetails,
    getAccountNotes,
    getActivities,
    getBalances,
    getCorrespondent,
    getAccountOwners,
    getTradingRestrictions,
    getAccountPositions,
    getMarginCalls,
    getMargins,
    getAmountAvailable,
    getSecuremailLinks,
  ])

  const { showAppContent } = useAuth()

  return (
    <>
      {showAppContent ? (
        <VStack paddingBottom="5rem">
          <AccountSearchBar
            hasDatePicker
            accountNumber={match.params.accountNumber}
            width="100%"
          />
          <AccountSummaryHeader
            accountNumber={match.params.accountNumber}
            pageTitle="SUMMARY"
            accountDetails={accountDetails}
            margins={margins}
            marginCalls={marginCalls}
            amountAvailable={amountAvailable}
            isLoading={
              loadingAccountDetails ||
              loadingMargins ||
              loadingMarginCalls ||
              loadingAmountAvailable ||
              loadingSecuremailLinks
            }
            width="100%"
            documentsLinks={securemailLinks || {}}
          />
          <HStack
            paddingX="1rem"
            alignItems="flex-start"
            overflowY="scroll"
            width="100%"
          >
            <VStack width="60%">
              <AccountInformation width="100%" isSummary={true} />
            </VStack>
            <VStack paddingLeft="3rem" width="40%">
              {!loadingBalances && (
                <BalancesTable
                  balances={balances}
                  paddingBottom="2rem"
                  width="100%"
                />
              )}
              {!loadingMargins && (
                <MarginsTable
                  margins={margins}
                  paddingBottom="2rem"
                  width="100%"
                />
              )}
              {!loadingPositions && (
                <PositionsTable
                  width="100%"
                  positions={accountPositions}
                  accountNumber={match.params.accountNumber}
                  paddingBottom="2rem"
                />
              )}
            </VStack>
          </HStack>
          <Stack width="100%" paddingX="1rem">
            {!loadingActivities && (
              <RecentActivityTable
                accountNumber={match.params.accountNumber}
                activities={activities}
                processDate={processDate}
              />
            )}
          </Stack>
        </VStack>
      ) : (
        <ApexLogin />
      )}
    </>
  )
}

export default AccountSummaryPage
